import { useCallback, useEffect, useRef, useState } from "react";
import { getAudioStatus, synthesizeSpeech } from "../utils/apiUtil";
import { useObjectUrls } from "./useObjectUrls";

// Whether TTS is configured is a per-deployment constant, so remounting the
// chat window must not re-request it. Only a successful resolution is
// memoized: a transient failure (cold server, blip, auth-token race at
// mount) resets statusPromise to null so the next mount retries, instead of
// pinning the tab to "disabled" for the rest of the session.
let statusPromise: Promise<{ ttsEnabled: boolean }> | null = null;
function fetchStatusOnce(token?: string): Promise<{ ttsEnabled: boolean }> {
  if (!statusPromise) {
    statusPromise = getAudioStatus(token).catch(() => {
      statusPromise = null;
      return { ttsEnabled: false };
    });
  }
  return statusPromise;
}

// A one-sample silent MP3. Playing this inside the click that turns autoplay
// on is what buys the right to call play() programmatically afterwards:
// Safari only allows scripted playback on an element that has already played
// from a user gesture. Autoplay deliberately does not survive a reload, so
// there is always a fresh click to prime from.
const SILENT_MP3 =
  "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tAwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAABAAABbgC1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW1tf////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAAW6MSoBLAAAAAAAAAAAAAAAAAAAA//sQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

export function useTextToSpeech(getToken: () => string | undefined) {
  const [ttsEnabled, setTtsEnabled] = useState(false);
  // Deliberately not persisted: starting every load with autoplay off means
  // the toggle click itself is always available as the priming gesture, so no
  // reply can be blocked by the browser's autoplay policy.
  const [autoplay, setAutoplay] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  const { create } = useObjectUrls();
  // Replaying a reply is free and instant — which matters when auditioning the
  // same line repeatedly to judge voice quality.
  const cacheRef = useRef<Map<string, string>>(new Map());
  // One element for the whole window, so starting a new reply implicitly stops
  // whichever one was already speaking. No cross-message coordination needed.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Bumped at the start of every toggle() call. A call whose token no longer
  // matches the latest one has been superseded by a later click (e.g.
  // clicking message B while message A's synthesis is still in flight) and
  // must not touch loadingId/playingId/errorId — only the fetch it kicked
  // off is still allowed to finish and populate the cache.
  const requestIdRef = useRef(0);

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    let cancelled = false;
    fetchStatusOnce(getTokenRef.current()).then((status) => {
      if (!cancelled) setTtsEnabled(status.ttsEnabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      audioRef.current = audio;
    }
    return audioRef.current;
  }, []);

  // useObjectUrls already revokes every URL it created on unmount; all this
  // needs to do is silence playback.
  useEffect(() => () => audioRef.current?.pause(), []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingId(null);
  }, []);

  // Unlocks the shared audio element for later scripted playback. Failure is
  // ignored: a browser that refuses even this is one where autoplay was never
  // going to work, and the on-demand speaker buttons still function.
  const toggleAutoplay = useCallback(() => {
    if (autoplay) {
      setAutoplay(false);
      return;
    }
    // Nothing to unlock if the element is mid-playback: it has already been
    // played from a gesture, and priming would cut the current reply off.
    if (!playingId) {
      const audio = getAudio();
      audio.src = SILENT_MP3;
      void audio.play().then(
        () => audio.pause(),
        () => undefined,
      );
    }
    setAutoplay(true);
  }, [autoplay, getAudio, playingId]);

  const toggle = useCallback(
    async (messageId: string, text: string, personaId: string) => {
      if (playingId === messageId) {
        stop();
        return;
      }
      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestIdRef.current === requestId;
      stop();
      setErrorId(null);

      let url = cacheRef.current.get(messageId);
      if (!url) {
        setLoadingId(messageId);
        try {
          const blob = await synthesizeSpeech(
            text,
            personaId,
            getTokenRef.current(),
          );
          url = create(blob);
          // Worth keeping even if superseded: the fetch already happened,
          // and caching it means a later replay is instant instead of
          // re-fetching.
          cacheRef.current.set(messageId, url);
        } catch {
          if (isCurrent()) setErrorId(messageId);
          return;
        } finally {
          // Ownership-based, not currency-based: the cache-hit path below
          // never touches loadingId, so a superseded call (e.g. clicking a
          // cached message B while A is still synthesizing) must still clear
          // its own spinner here, or A's button is stuck disabled forever.
          setLoadingId((prev) => (prev === messageId ? null : prev));
        }
      }

      // A later click (on this message or another one) has taken over —
      // don't start playback or touch state on its behalf.
      if (!isCurrent()) return;

      const audio = getAudio();
      audio.src = url;
      setPlayingId(messageId);
      try {
        await audio.play();
      } catch {
        if (isCurrent()) {
          setPlayingId(null);
          setErrorId(messageId);
        }
      }
    },
    [create, getAudio, playingId, stop],
  );

  return {
    ttsEnabled,
    autoplay,
    toggleAutoplay,
    playingId,
    loadingId,
    errorId,
    toggle,
  };
}
